import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { useAppContext } from '../../../context/AppContext';
import { updateData } from '../../../services';
import ConfirmDialog from '../../helpers/ConfirmDialog';

const STATUS_CONFIG = {
    op_package: {
        completedField: 'op_package_completed',
        tagLabel: 'Optimization Package',
        titles: {
            completed: 'Optimization package completed',
            clickable: 'Click to mark optimization package as complete',
            pending: 'Optimization package has been ordered',
        },
        confirmTitle: 'Mark Optimization Package as Complete',
        confirmMessage:
            'Are you sure you want to mark the optimization package as complete for this project?',
        confirmDetail:
            'This will change the tag to green to indicate the optimization package is complete.',
        successMessage: 'Optimization package marked as complete!',
    },
    pe_stamps: {
        completedField: 'pe_stamps_completed',
        tagLabel: 'PE Stamps',
        titles: {
            completed: 'Stamped engineering completed',
            clickable: 'Click to mark stamped engineering as complete',
            pending: 'PE stamps have been ordered',
        },
        confirmTitle: 'Mark Stamped Engineering as Complete',
        confirmMessage:
            'Are you sure you want to mark the stamped engineering as complete for this project?',
        confirmDetail:
            'This will change the tag to green to indicate the stamped engineering is complete.',
        successMessage: 'Stamped engineering marked as complete!',
    },
};

export const MarkProjectStatusComplete = ({ project, user, statusKey }) => {
    const { token, base_url, setProjects } = useAppContext();
    const config = STATUS_CONFIG[statusKey];

    if (!config) {
        console.error(`Unknown project status key: ${statusKey}`);
        return null;
    }

    const isCompleted = project[config.completedField];
    const isSuperAdmin = user && user.is_super_admin;
    const isClickable = !isCompleted && isSuperAdmin;
    const tagClass = isCompleted
        ? 'status-chip status-chip--stamp status-chip--success'
        : 'status-chip status-chip--stamp status-chip--pending';

    let tagTitle = config.titles.pending;
    if (isCompleted) {
        tagTitle = config.titles.completed;
    } else if (isSuperAdmin) {
        tagTitle = config.titles.clickable;
    }

    const tag = (
        <div
            className={`${tagClass} ${isClickable ? 'cursor-pointer' : 'cursor-default'}`}
            title={tagTitle}
        >
            {config.tagLabel} {isCompleted ? 'Complete' : 'Ordered'}
        </div>
    );

    if (!isClickable) {
        return tag;
    }

    return (
        <ConfirmDialog
            trigger={tag}
            title={config.confirmTitle}
            message={config.confirmMessage}
            entityLabel={`Project: ${project?.project_name}`}
            detail={config.confirmDetail}
            confirmLabel="Mark Complete"
            confirmIcon={faCheck}
            successMessage={config.successMessage}
            errorMessages={{
                notFound: 'Project not found',
                client: 'Invalid project data. Please check your input.',
                default: 'Failed to update project. Please try again.',
            }}
            onConfirm={async () => {
                const updatedProject = await updateData(
                    token,
                    `${base_url}/apis/projects/${project.id}`,
                    { [config.completedField]: true }
                );

                if (!updatedProject.project) return false;

                setProjects((prevProjects) =>
                    prevProjects.map((p) =>
                        p.id === project.id ? updatedProject.project : p
                    )
                );
            }}
        />
    );
};

export const MarkOpPackageCompleteForm = ({ project, user }) => (
    <MarkProjectStatusComplete project={project} user={user} statusKey="op_package" />
);

export const MarkStampedCompleteForm = ({ project, user }) => (
    <MarkProjectStatusComplete project={project} user={user} statusKey="pe_stamps" />
);
